import { LESSON_KIND, LESSON_STATE } from '@theailab-dev/parchment';
import type { LessonKind } from '@theailab-dev/parchment';
import DOMPurify from 'dompurify';
import React from 'react';
import { HiPencil, HiTrash } from 'react-icons/hi';
import { useParams, useNavigate } from 'react-router-dom';
import { deleteLesson } from '../../api/lessonApi';
import { DetailPageLayout } from '../../components/Layout/DetailPageLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { InfoItem } from '../../components/ui/InfoItem';
import { VideoManagementSection } from '../../components/VideoManagementSection/VideoManagementSection';
import { useLessonDetail } from '../../hooks/useLessonDetail';
import { formatDateTime } from '../../utils/dateTime';
import { getMaterialDisplayName } from '../../utils/materialUrl';

/**
 * LessonDetail 컴포넌트 - 레슨 상세 페이지
 */
const LessonDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const lessonId = parseInt(id || '0', 10);

  const { data: lesson, isLoading, error } = useLessonDetail(lessonId);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const handleEdit = () => {
    if (!lesson) return;
    navigate(`/dashboard/courses/${lesson.course.id}/chapters/${lesson.chapter.id}/lessons/${lesson.id}/edit`);
  };

  const handleDelete = async () => {
    if (!lesson) return;
    setIsDeleting(true);
    try {
      await deleteLesson(lesson.id);
      navigate(`/dashboard/courses/${lesson.course.id}`);
    } catch (error) {
      console.error('Failed to delete lesson:', error);
      alert('레슨 삭제에 실패했습니다.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  /**
   * 날짜 포맷팅
   */
  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '-';

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch {
      return '-';
    }
  };

  /**
   * duration 포맷팅 (초 → 시:분:초)
   */
  const formatDuration = (duration: string | null) => {
    if (!duration) return '-';

    // duration이 이미 포맷된 문자열이면 그대로 반환
    if (duration.includes(':')) return duration;

    // 초 단위로 변환
    const seconds = parseInt(duration, 10);
    if (isNaN(seconds)) return '-';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  /**
   * 레슨 종류 배지 variant 반환
   */
  const getKindBadgeVariant = (kind: LessonKind) => {
    switch (kind) {
      case LESSON_KIND.VIDEO:
        return 'blue';
      case LESSON_KIND.PDF:
        return 'purple';
      case LESSON_KIND.TEXT:
        return 'success';
      case LESSON_KIND.RETAKE:
        return 'red';
      case LESSON_KIND.ZOOM:
        return 'primary';
      case LESSON_KIND.EXAM:
        return 'warning';
      case LESSON_KIND.TEMP:
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  if (!lessonId) return null;

  return (
    <DetailPageLayout
      title="레슨 상세 정보"
      subtitle="레슨의 상세 정보와 콘텐츠를 확인합니다."
      isLoading={isLoading}
      isError={!!error}
      errorMessage={error?.message}
      badges={
        lesson && (
          <Badge variant={lesson.state === LESSON_STATE.NORMAL ? 'success' : 'neutral'}>
            {lesson.state === LESSON_STATE.NORMAL ? '정상' : '삭제됨'}
          </Badge>
        )
      }
      actions={
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={handleEdit} leftIcon={<HiPencil />}>
            수정
          </Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)} leftIcon={<HiTrash />}>
            삭제
          </Button>
        </div>
      }>
      {lesson && (
        <>
          <ConfirmModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            title="레슨 삭제"
            message={`'${lesson.title}' 레슨을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`}
            type="warning"
            buttons={[
              {
                label: '취소',
                onClick: () => setShowDeleteModal(false),
                variant: 'secondary',
              },
              {
                label: '삭제',
                onClick: handleDelete,
                variant: 'danger',
                isLoading: isDeleting,
              },
            ]}
          />
          <div className="space-y-6">
            {/* 소속 정보 */}
            <Card title="소속 정보">
              <div className="space-y-4">
                <InfoItem
                  label="강의"
                  value={
                    lesson.course ? (
                      <span
                        className="cursor-pointer font-medium text-indigo-600 hover:underline"
                        onClick={() => navigate(`/dashboard/courses/${lesson.course.id}`)}>
                        {lesson.course.title}
                      </span>
                    ) : (
                      '-'
                    )
                  }
                />
                <InfoItem
                  label="챕터"
                  value={
                    lesson.chapter ? (
                      <span>
                        {lesson.chapter.seq}. {lesson.chapter.title}
                      </span>
                    ) : (
                      '-'
                    )
                  }
                />
              </div>
            </Card>

            {/* 기본 정보 */}
            <Card title="레슨 정보">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <InfoItem label="제목" value={lesson.title} />
                </div>
                <InfoItem label="순서" value={`${lesson.seq}번`} />
                <InfoItem
                  label="종류"
                  value={<Badge variant={getKindBadgeVariant(lesson.kind)}>{lesson.kind}</Badge>}
                />
                <InfoItem label="강의 시간" value={formatDuration(lesson.duration)} />
                <InfoItem
                  label="미리보기"
                  value={
                    <Badge variant={lesson.isPreviewable ? 'success' : 'neutral'}>
                      {lesson.isPreviewable ? '가능' : '불가능'}
                    </Badge>
                  }
                />
                <InfoItem label="생성일" value={formatDate(lesson.createdAt)} />
                <InfoItem label="수정일" value={formatDate(lesson.updatedAt)} />
                <InfoItem
                  label="레슨 자료"
                  value={
                    (lesson as { materialsUrl?: string | null }).materialsUrl ? (
                      <a
                        href={(lesson as { materialsUrl: string }).materialsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-indigo-600 hover:text-indigo-700 hover:underline"
                        title={(lesson as { materialsUrl: string }).materialsUrl}>
                        {getMaterialDisplayName((lesson as { materialsUrl: string }).materialsUrl)}
                      </a>
                    ) : (
                      '-'
                    )
                  }
                />
                {lesson.kind === LESSON_KIND.ZOOM ? (
                  <>
                    <InfoItem label="시작 일시" value={formatDateTime(lesson.startAt)} />
                    <InfoItem label="종료 일시" value={formatDateTime((lesson as any).endAt)} />
                  </>
                ) : (
                  lesson.startAt && <InfoItem label="시작일" value={formatDate(lesson.startAt)} />
                )}
              </div>
            </Card>

            {/* 콘텐츠 정보 */}
            {(lesson.content || lesson.videoUrl || lesson.joinUrl) && (
              <Card title="콘텐츠">
                <div className="space-y-6">
                  {lesson.content && (
                    <InfoItem
                      label="설명"
                      value={
                        <div
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(lesson.content, {
                              ALLOWED_TAGS: [
                                'p',
                                'br',
                                'b',
                                'i',
                                'u',
                                'strong',
                                'em',
                                'a',
                                'img',
                                'span',
                                'div',
                                'ul',
                                'ol',
                                'li',
                                'h1',
                                'h2',
                                'h3',
                                'h4',
                                'h5',
                                'h6',
                              ],
                              ALLOWED_ATTR: [
                                'href',
                                'src',
                                'alt',
                                'title',
                                'target',
                                'rel',
                                'style',
                                'class',
                                'data-emoji',
                                'aria-label',
                                'draggable',
                                'loading',
                              ],
                            }),
                          }}
                        />
                      }
                    />
                  )}
                  {/* 동영상 섹션 - VIDEO 타입일 때만 표시 */}
                  {lesson.kind === LESSON_KIND.VIDEO ||
                    (lesson.kind === LESSON_KIND.RETAKE && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-slate-700">동영상</h4>
                        <VideoManagementSection videoUrl={lesson.videoUrl} />
                      </div>
                    ))}
                  {/* 참여 URL - ZOOM 타입일 때만 표시 */}
                  {lesson.joinUrl && (
                    <InfoItem
                      label="참여 URL"
                      value={
                        <a
                          href={lesson.joinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all text-indigo-600 hover:text-indigo-700 hover:underline">
                          {lesson.joinUrl}
                        </a>
                      }
                    />
                  )}
                </div>
              </Card>
            )}
          </div>
        </>
      )}
    </DetailPageLayout>
  );
};

export default LessonDetail;
